import React, { useContext } from 'react'
import { observer } from 'mobx-react'
import { PermissionActions } from '@App/enum/permission-actions'
import UserActionPopup from '@Components/Popups/UserActionPopup'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import { deleteRightsholderUser } from '@App/api/routes/rightsholder/rightsholderUser'
import Navigation from '@App/navigation'
import { UserInfo } from '@Entities/user/user'
import { useNavigate, useParams } from 'react-router'
import { MyUserGroup } from '@Scenes/RightsholderScene/RightsholderMaintenanceUserPermissionsProfile'
import ChangePermissionPopup from '@Scenes/RightsholderScene/RightsholderMaintenanceUserPermissionsProfile/UserPermissionsModal/ChangePermissionPopup'
import { PARAM } from '@App/navigation/param'

interface UserPermissionsModalProps {
    actionType: PermissionActions | undefined
    rightsholderUser: UserInfo
    onCloseConfirmPopup: () => void
    editActionsGroups: MyUserGroup[]
    myUserGroups: MyUserGroup[]
    setRightsholderUser: (userInfo: UserInfo) => void
    setUsersGroup: (user: UserInfo) => void
    getUsersForUser: (user: UserInfo) => void
    onCancelEdit: (user: UserInfo) => void
}

const UserPermissionsModal = (props: UserPermissionsModalProps) => {
    const params = useParams<typeof PARAM>()
    const navigate = useNavigate()
    const { preloaderStore, popupStore, userStore, rightsholderUserStore } = useContext<RootStore>(StoresContext)
    const {
        actionType,
        rightsholderUser,
        onCloseConfirmPopup,
        editActionsGroups,
        myUserGroups,
        setRightsholderUser,
        setUsersGroup,
        getUsersForUser,
        onCancelEdit,
    } = props

    const onResentInvite = async () => {
        const user = rightsholderUser

        if (user) {
            preloaderStore.openTransparentPreloader(true)
            const { firstName, lastName, email, groups } = user
            try {
                const formData: Partial<UserInfo> = { firstName, lastName, email, groups }
                await rightsholderUserStore.inviteRightsholderUser(formData)
                popupStore.setAlertData(`Invite was resent`, false)
            } catch (error) {
                popupStore.setAlertFromError(error)
            }
            onCloseConfirmPopup()
            preloaderStore.openTransparentPreloader(false)
        }
    }

    const onRemoveUser = async () => {
        const currentRightsHolderId = Number(params.rightsholderId)
        const user = rightsholderUser

        if (user?.exposedId) {
            preloaderStore.openTransparentPreloader(true)
            const { exposedId, firstName, lastName } = user

            try {
                await deleteRightsholderUser(currentRightsHolderId, exposedId)
                navigate(Navigation.rightholder.maintenance.userPermissions(currentRightsHolderId))
                popupStore.setAlertData(`User ${firstName + ' ' + lastName} was removed`, false)
            } catch (error) {
                popupStore.setAlertFromError(error)
            }
            onCloseConfirmPopup()
            preloaderStore.openTransparentPreloader(false)
        }
    }

    switch (actionType) {
        case PermissionActions.changePermissions:
            return (
                <ChangePermissionPopup
                    rightsholderUser={rightsholderUser}
                    setRightsholderUser={setRightsholderUser}
                    myUserGroups={myUserGroups}
                    editActionsGroups={editActionsGroups}
                    getUsersForUser={getUsersForUser}
                    onCloseConfirmPopup={onCloseConfirmPopup}
                    setUsersGroup={setUsersGroup}
                    onCancelEdit={onCancelEdit}
                />
            )
        case PermissionActions.resentInvite:
            return (
                <UserActionPopup
                    title="Resend Invite?"
                    user={rightsholderUser}
                    onCancel={onCloseConfirmPopup}
                    onConfirm={onResentInvite}
                />
            )
        case PermissionActions.removeUser:
            return (
                <UserActionPopup
                    title="Remove User?"
                    user={rightsholderUser}
                    onCancel={onCloseConfirmPopup}
                    onConfirm={onRemoveUser}
                    disabled={userStore.isInternal}
                />
            )
        default:
            return <></>
    }
}

export default observer(UserPermissionsModal)
